import {
  IMPACT_OWNER_FIRSTNAME,
  IMPACT_OWNER_LASTNAME,
  IMPACT_REPORT_CATEGORY_DISPLAY_NAME,
  IMPACT_REPORT_KPI_PREFIX,
  IMPACT_REPORT_LABEL_ID,
  ImpactReportCategory,
  ImpactReportUseCase,
  MANAGER_FIRSTNAME,
  MANAGER_LASTNAME,
  quantifyUnits,
} from '@/impactReports/impactReport.types';
import { ColDef, GridApi, IRowNode, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { t } from 'i18next';
import _ from 'lodash';
import { UseCaseNameCellRenderer } from '@/impactReports/cellRenderers/UseCaseNameCellRenderer.atom';
import { UseCaseStatusCellRenderer } from '@/impactReports/cellRenderers/UseCaseStatusCellRenderer.atom';
import { ColumnDefinitionOutputV1, sqFormulasApi, sqGraphQLApi, sqItemsApi } from '@/sdk';
import { MAX_TABLE_ROWS } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { MaterializedTablePropertyColumnInput } from '@/tableDefinitionEditor/materializedTable.types';
import {
  formatNumberAsValue,
  getCategoryDisplayString,
  getCustomCategoryTitle,
  getImpactTypeDisplayString,
  getKPIDisplayName,
  getKPIItemColumnName,
  getKPIUnit,
  getStageDisplayString,
} from '@/impactReports/impactReportDisplay.utilities';
import { validateGuid } from '@/utilities/utilities';
import { logError } from '@/utilities/logger';

export function getColumnDefinitions(
  categories: ImpactReportCategory[] | undefined,
  showSite: boolean,
  showAssetPath: boolean,
  kpiColumns: ColumnDefinitionOutputV1[] = [],
): ColDef[] {
  const categoryCustomColumns = categories
    ? categories.map((category: ImpactReportCategory) => {
        const cat = category.category as any;
        const fieldName = getCustomCategoryTitle(category);
        const options = category.options;
        return {
          field: cat.id,
          headerName: fieldName,
          flex: 1,
          floatingFilter: true,
          filter: 'agSetColumnFilter',
          chartDataType: 'category' as const,
          valueGetter: (rowData: ValueGetterParams<string, string>) => {
            if (!rowData || !rowData.data) return;
            return getCategoryDisplayString(rowData.data[cat.id], options, rowData.data[cat.id]);
          },
          context: { canAggregateBy: true },
        };
      })
    : [];

  const kpiCustomColumns = kpiColumns?.map(({ columnName }) => {
    return {
      field: columnName,
      headerName: getKPIDisplayName(columnName),
      flex: 1,
      cellClass: 'right-align',
      headerClass: 'pull-right',
      floatingFilter: true,
      filter: 'agSetColumnFilter',
      aggFunc: 'sum',
      chartDataType: 'series' as const,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        const unit = getKPIUnit(columnName);
        const formatterFn = getValueFormatter(unit);
        return params.value ? formatterFn(params.value, unit) : '';
      },
      valueGetter: (rowData: ValueGetterParams<string, string>) => {
        if (!rowData || !rowData.data) return;
        const value = _.get(rowData.data, columnName);
        const number = Number(value);
        return isNaN(number) ? 0 : Math.round(number);
      },
    };
  });

  const assetColumn = {
    field: SeeqNames.ImpactReport.ImpactAsset,
    headerName: t('IMPACT_REPORT.TABLE_HEADERS.ASSET'),
    flex: 1,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    aggFunc: 'first',
    chartDataType: 'category' as const,
    context: { canAggregateBy: true },
  };

  const siteColumn = {
    field: SeeqNames.ImpactReport.ImpactSite,
    headerName: t('IMPACT_REPORT.TABLE_HEADERS.SITE'),
    flex: 1,
    floatingFilter: true,
    filter: 'agSetColumnFilter',
    aggFunc: 'first',
    chartDataType: 'category' as const,
    context: { canAggregateBy: true },
  };

  return [
    { field: 'value', headerName: '# of Reports', hide: true, valueGetter: (rowData: any) => 1 },
    {
      field: SeeqNames.ImpactReport.ImpactName,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.NAME'),
      flex: 2,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      filterValueGetter: (params: ValueGetterParams) => `${params.data[SeeqNames.ImpactReport.ImpactName]}`,
      cellRenderer: UseCaseNameCellRenderer,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data) return;
        return {
          name: rowData.data[SeeqNames.ImpactReport.ImpactName],
          worksheetId: rowData.data[SeeqNames.ImpactReport.AssignedToWorksheet],
          type: rowData.data[SeeqNames.ImpactReport.ImpactBasedOn],
        };
      },
    },
    {
      field: SeeqNames.ImpactReport.ImpactStatus,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.STATUS'),
      flex: 1,
      floatingFilter: true,
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      filterParams: {
        valueFormatter: (params: any) => {
          return getStageDisplayString(params.value);
        },
      },
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data) return;
        return rowData.data[SeeqNames.ImpactReport.ImpactStatus]?.toLowerCase().trim();
      },
      cellRenderer: UseCaseStatusCellRenderer,
      context: { canAggregateBy: true },
    },
    ...categoryCustomColumns,
    showAssetPath ? assetColumn : {},
    showSite ? siteColumn : {},
    {
      field: SeeqNames.ImpactReport.ImpactOwner,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.OWNER'),
      filter: 'agSetColumnFilter',
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data) return;
        return `${rowData.data[IMPACT_OWNER_FIRSTNAME] ?? ''} ${rowData.data[IMPACT_OWNER_LASTNAME] ?? ''}`;
      },
      context: { canAggregateBy: true },
    },
    {
      field: SeeqNames.ImpactReport.Manager,
      filter: 'agSetColumnFilter',
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.MANAGER'),
      flex: 2,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        const first = rowData.data[MANAGER_FIRSTNAME];
        const last = rowData.data[MANAGER_LASTNAME];
        return `${first ?? ''} ${last ?? ''}`;
      },
      chartDataType: 'category' as const,
      aggFunc: 'first',
      context: { canAggregateBy: true },
    },
    {
      field: SeeqNames.ImpactReport.ImpactSavingsType,
      headerName: t('IMPACT_REPORT.TABLE_HEADERS.TYPE'),
      flex: 2,
      valueGetter: (rowData) => {
        if (!rowData || !rowData.data || !rowData.node) return;
        if (rowData.node.rowPinned === 'bottom') return '';
        return getImpactTypeDisplayString(rowData.data[SeeqNames.ImpactReport.ImpactSavingsType]);
      },
      chartDataType: 'category' as const,
      floatingFilter: true,
      filter: 'agSetColumnFilter',
      context: { canAggregateBy: true },
    },
    ...kpiCustomColumns,
  ];
}
// determine the formula to be used to calculate the impact values (monetary as well as custom kpi)
export async function getImpactFormula(
  rowData: ImpactReportUseCase,
  lookupColumn: string,
  displayStartDate: string,
  displayEndDate: string,
): Promise<string | undefined> {
  if (!rowData) return;

  const impactType = rowData[SeeqNames.ImpactReport.ImpactSavingsType];
  const impactFrequency = rowData[SeeqNames.ImpactReport.ImpactFrequency];
  const startDate = rowData[SeeqNames.ImpactReport.ImpactStartDate];
  const endDate = rowData[SeeqNames.ImpactReport.ImpactEndDate];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const impactAmountOrItemId = rowData[lookupColumn];
  const validItem = validateGuid(impactAmountOrItemId);
  if (!startDate || !impactAmountOrItemId || (!impactFrequency && validItem)) {
    return undefined;
  }

  let formula;
  // first determine if we are dealing with a custom calculation - we determine that by checking the value of the
  // column and checking that it's a valid guid.
  if (validItem) {
    // we have to fetch the item to obtain its unit to ensure proper formula selection
    const { data } = await sqItemsApi.getProperty({
      id: impactAmountOrItemId,
      propertyName: SeeqNames.Properties.ValueUom,
    });
    const unit = data.value ?? '';

    const aggregationFn = unit.indexOf('/') < 0 ? 'sum()' : 'totalized()';
    if (!endDate) {
      formula = `0.tosignal().spliceDate($customSavingsSignal,'${startDate}').aggregate(${aggregationFn},capsule('${displayStartDate}', '${displayEndDate}'))`;
    } else {
      formula = `$customSavingsSignal.within(condition(capsule('${startDate}','${endDate}'))).aggregate(${aggregationFn},capsule('${displayStartDate}', '${displayEndDate}'))`;
    }
    return formula;
  } else if (impactType === 'continuous') {
    if (!endDate) {
      formula = `(condition(10y,capsule('${startDate}', now())).setUncertainty(1h)).intersect(days().setProperty('Impact Estimate',${impactAmountOrItemId}/${impactFrequency}),keepProperties())`;
    } else {
      formula = `(condition(10y,capsule('${startDate}','${endDate}'))).intersect(past()).intersect(days().setProperty('Impact Estimate',${impactAmountOrItemId}/${impactFrequency}),keepProperties()).setUncertainty(1h)`;
    }
  } else if (impactType === 'oneTime') {
    formula = `condition(10y,capsule('${startDate}', '${startDate}').setProperty('Impact Estimate', ${impactAmountOrItemId}))`;
  }

  return `(${formula}).toSignal('Impact Estimate').sum(capsule('${displayStartDate}','${displayEndDate}'))`;
}

/**
 * The impact is calculated based on a Formula that is stored on the use case.
 * Before we can calculate the impact, we need to make sure that we add the KPI columns to the table data so we can
 * reference user provided information on edit.
 */
export async function decorateMaterializedTableWithImpactValue(
  rawTableData: ImpactReportUseCase[],
  kpiColumns: ColumnDefinitionOutputV1[],
  startDate: string,
  endDate: string,
) {
  const tableData = rawTableData.map((item) => {
    const kpiItemColumns = Object.keys(item).filter((key) => key.startsWith(IMPACT_REPORT_KPI_PREFIX));

    let result: ImpactReportUseCase = { ...item };

    kpiItemColumns.forEach((column) => {
      result = {
        ...result,
        [getKPIItemColumnName(column)]: _.get(item, column),
      };
    });

    return result;
  });

  return await Promise.all(
    tableData.map(async (row: any) => {
      await Promise.all(
        kpiColumns.map(async (kpiColumn) => {
          row[kpiColumn.columnName] = await calculateImpactValue(row, kpiColumn, startDate, endDate);
        }),
      );

      return row;
    }),
  );
}
export async function calculateImpactValue(
  row: Record<string, any>,
  kpiColumn: ColumnDefinitionOutputV1,
  startDate: string,
  endDate: string,
) {
  let formula;
  try {
    formula = await getImpactFormula(row as unknown as ImpactReportUseCase, kpiColumn.columnName, startDate, endDate);
  } catch (e) {
    logError(`Failed to get impact formula for ${kpiColumn.columnName}`);
    return;
  }
  if (formula) {
    try {
      const parameters = [];
      if (formula.indexOf('$customSavingsSignal') > -1) {
        parameters.push(`customSavingsSignal=${row[kpiColumn.columnName]}`);
      }
      const formulaResponse = await sqFormulasApi.runFormula({
        parameters,
        formula,
      });
      return formulaResponse.data?.scalar?.value;
    } catch (e) {
      logError(`Failed to calculate impact value for ${kpiColumn.columnName}`);
      row[kpiColumn.columnName] = 0; // fallback to 0 if the formula fails
    }
  }
}
/**
 * This returns the Materialized Table for the Impact Report table without the "likes" rows
 * this is accomplished by filtering to only the desired category context labels, not the "like" label.
 * In theory, we should be able to filter by category_id but that doesn't work yet
 * (https://seeq.atlassian.net/browse/CRAB-45806)
 *
 * The GraphQL endpoint returns an array of headers and an array of rows where rows can be duplicated.
 * There will always be one row per assigned context label (that's why we filter out the "like" label), but there can
 * also be duplicates for no apparent reason :shrug:
 *
 * The AGGrid table expects context category ids to be provided as the column name, and the value renderer will
 * parse the context label id to display the label name.
 */
export async function getTableWithoutLikes(tableId: string, categories: ImpactReportCategory[]) {
  const allLabelIds = _.chain(categories)
    .map((category) => category.options)
    .map((options) => options?.map((opt) => opt.value))
    .flatten()
    .value();

  const properties: MaterializedTablePropertyColumnInput[] = [
    {
      uuidColumn: SeeqNames.ImpactReport.ImpactOwner,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
    {
      uuidColumn: SeeqNames.ImpactReport.Manager,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
  ];

  const { data } = await sqGraphQLApi.graphql({
    query:
      '\nquery GetTable($id: String!, $filter: FilterInput, $limit: Int!, $columnsToInclude: [String!],' +
      ' $propertiesToInclude: [PropertiesForItemUUIDColumnInput!], ) {\n' +
      '  table(id: $id, filter: $filter, limit: $limit, columnsToInclude: $columnsToInclude, propertiesToInclude: $propertiesToInclude)' +
      ' {\n    rows\n' +
      '    headers {\n      name\n      type\n    }\n    hasMore\n  }\n}\n',
    variables: {
      id: tableId,
      limit: MAX_TABLE_ROWS,
      columnsToInclude: [],
      propertiesToInclude: properties,
      // filter: {
      //    we need to have a "NOT" option for the filterType so we can filter out the like ones that way - we
      //    can't use allLabelIds above as this will lead to un-categorized reports not being displayed
      //   valueFilter: {
      //     columnName: 'context_labels.label_id',
      //     filterType: 'IN',
      //     value: allLabelIds,
      //   },
      // },
    },
  });

  const transformedData = transformTableResponseToObject(data.data.table?.rows, data.data.table?.headers);
  // we will now have multiple rows (each context category on its own row)
  const withLabels = mergeContextLabels(transformedData);
  return mergeRows(withLabels);
}

/**
 * Little helper function that creates an object with the column name as the key and the value as the value
 */
function transformTableResponseToObject(rows: any[], headers: Record<string, any>) {
  return rows.map((row) =>
    headers.reduce((obj: any, header: Record<string, any>, index: number) => {
      obj[header.name] = row[index] || null;
      return obj;
    }, {}),
  );
}

function mergeContextLabels(data: Record<string, any>[]) {
  return data.map((row) => {
    const categoryId = row['context_labels.category_id'];
    row[categoryId] = row['context_labels.label_id'];
    row[`${categoryId}_${IMPACT_REPORT_LABEL_ID}`] = row['context_labels.id'];
    row[`${IMPACT_REPORT_CATEGORY_DISPLAY_NAME}_${categoryId}`] = row['context_labels.label_name'];
    return row;
  });
}

function mergeRows(data: Record<string, any>[]) {
  return Object.values(
    data.reduce((acc, item) => {
      const key = `${item[SeeqNames.MaterializedTables.DatumIdColumn]}-${
        item[SeeqNames.MaterializedTables.ItemIdColumn]
      }`;
      if (!acc[key]) {
        acc[key] = {
          [SeeqNames.MaterializedTables.DatumIdColumn]: item[SeeqNames.MaterializedTables.DatumIdColumn],
          [SeeqNames.MaterializedTables.ItemIdColumn]: item[SeeqNames.MaterializedTables.ItemIdColumn],
        };
      }
      Object.assign(acc[key], item);
      return acc;
    }, {}),
  );
}

export async function getImpactReportByWorksheet(tableId: string, worksheetId: string, workbookId: string) {
  const properties: MaterializedTablePropertyColumnInput[] = [
    {
      uuidColumn: SeeqNames.ImpactReport.ImpactOwner,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
    {
      uuidColumn: SeeqNames.ImpactReport.Manager,
      propertyNames: [SeeqNames.Properties.FirstName, SeeqNames.Properties.LastName],
    },
  ];
  const { data } = await sqGraphQLApi.graphql({
    query:
      '\nquery GetTable($id: String!, $filter: FilterInput, $limit: Int!, $columnsToInclude: [String!], $propertiesToInclude: [PropertiesForItemUUIDColumnInput!]) {\n' +
      '  table(id: $id, filter: $filter, limit: $limit, columnsToInclude: $columnsToInclude, propertiesToInclude: $propertiesToInclude) {\n    rows\n' +
      '    headers {\n      name\n      type\n    }\n    hasMore\n  }\n}\n',
    variables: {
      id: tableId,
      limit: MAX_TABLE_ROWS,
      columnsToInclude: [],
      propertiesToInclude: properties,
      filter: {
        compositeFilter: {
          filter1: {
            valueFilter: {
              columnName: SeeqNames.ImpactReport.AssignedToWorksheet,
              filterType: 'IN',
              value: [worksheetId],
            },
          },
          filter2: {
            valueFilter: {
              columnName: SeeqNames.ImpactReport.AssignedToWorkbook,
              filterType: 'IN',
              value: [workbookId],
            },
          },
          operation: 'AND',
        },
      },
    },
  });
  const transformedData = transformTableResponseToObject(data.data.table?.rows, data.data.table?.headers);
  // we will now have multiple rows (each context category on its own row)
  const withLabels = mergeContextLabels(transformedData);
  const mergedData = mergeRows(withLabels);
  // now w
  return mergedData[0];
}

export function getKPITotals(api: GridApi, filteredOnly = false) {
  const totals: Record<string, number> = {};
  const maxImpacts: Record<string, number> = {};
  if (!api || !api.getColumnDefs()) return { totals, maxImpacts };
  const kpiColumns = api.getColumnDefs()!.filter((col) => _.get(col, 'field', '').startsWith(IMPACT_REPORT_KPI_PREFIX));

  const rows: IRowNode[] = [];
  if (filteredOnly) {
    api.forEachNodeAfterFilterAndSort((node) => {
      rows.push(node);
    });
  } else {
    api.forEachNode((node) => {
      rows.push(node);
    });
  }

  kpiColumns.forEach((col) => {
    let max = -1;
    const colId = _.get(col, 'field');
    totals[colId] = rows.reduce((sum, node) => {
      const value = Number(node.data[colId]);
      if (value > max) {
        max = value;
        maxImpacts[colId] = max;
      }
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  });

  return { totals, maxImpacts };
}

export function getValueFormatter(unit: string) {
  if (quantifyUnits.Currency.indexOf(unit) > -1) {
    return formatNumberAsValue;
  } else
    return (value: number, unit: string) => (!isFinite(value) ? '-' : `${Math.round(value).toLocaleString()} ${unit}`);
}
